import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NON_CUSTODIAL_CONNECTION_STATUS } from '../constants/wallet.constants';
import {
  isCustodialWallet,
  isNonCustodialWallet,
} from '../services/wallet/linkedWallet.utils';
import { notifyMissingTokenContractsIfNeeded } from '../services/wallet/nonCustodialTokenConfig';
import { isNonCustodialRpcBusy } from '../services/wallet/nonCustodialSession';
import { refreshActivePairBalance } from '../services/wallet/tradeBalanceRefresh.service';

/**
 * Keeps Header / Accounts activePairBalance in sync with the selected pair and wallet.
 * Mounted at Exchange level so balance loads even when BuySellTab is not visible (mobile layouts).
 */
export function useTradeBalanceSync({ isApprovingAllowance = false } = {}) {
  const dispatch = useDispatch();
  const selectedPair = useSelector((state) => state?.ats?.selectedPair);
  const selectedTradeWallet = useSelector((state) => state?.ats?.selectedTradeWallet);
  const nonCustodialConnectionStatus = useSelector(
    (state) => state?.ats?.nonCustodialConnectionStatus,
  );
  const ncRetryTimerRef = useRef(null);

  const isWalletReady =
    Boolean(selectedTradeWallet) &&
    (isCustodialWallet(selectedTradeWallet) ||
      nonCustodialConnectionStatus === NON_CUSTODIAL_CONNECTION_STATUS.CONNECTED);

  useEffect(() => {
    if (isApprovingAllowance || isNonCustodialRpcBusy()) return;
    if (!isWalletReady || !selectedPair?.baseCurrency || !selectedPair?.otherCurrency) {
      return;
    }

    refreshActivePairBalance(dispatch, { selectedPair, selectedTradeWallet }).then(() => {
      if (selectedTradeWallet && isNonCustodialWallet(selectedTradeWallet)) {
        notifyMissingTokenContractsIfNeeded(selectedTradeWallet, selectedPair);
      }
    });

    if (
      selectedTradeWallet &&
      isNonCustodialWallet(selectedTradeWallet) &&
      nonCustodialConnectionStatus === NON_CUSTODIAL_CONNECTION_STATUS.CONNECTED
    ) {
      if (ncRetryTimerRef.current) clearTimeout(ncRetryTimerRef.current);
      ncRetryTimerRef.current = setTimeout(() => {
        refreshActivePairBalance(dispatch, { selectedPair, selectedTradeWallet });
      }, 800);
    }

    return () => {
      if (ncRetryTimerRef.current) {
        clearTimeout(ncRetryTimerRef.current);
        ncRetryTimerRef.current = null;
      }
    };
  }, [
    dispatch,
    isApprovingAllowance,
    isWalletReady,
    nonCustodialConnectionStatus,
    selectedPair?.baseCurrency,
    selectedPair?.otherCurrency,
    selectedTradeWallet?.walletId,
    selectedTradeWallet?.coinName,
    selectedTradeWallet?.custodyType,
  ]);
}

export default useTradeBalanceSync;
